import Link from 'next/link';
import React, { useContext, useEffect, useState } from 'react'
import { MdOutlineArrowBackIos } from 'react-icons/md';
import { CommonStyle } from '../../styles/common-styles';
import backgroundImage from '../../public/images/backgroundImage/technician-bg.png';
import { ConfirmationResult, getAuth, RecaptchaVerifier } from 'firebase/auth';
import useUpdateProfile from '../../hooks/user/useUpdateProfile';
import { UserContext } from '../../contexts/UserContext';
import { ProfileViewModel } from '../../models/user';
import { e164PhoneNumber, isValidEmailAddress, isValidPhoneNumber, uniqueName } from '../../utils/util';
import router from 'next/router';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Spinner } from 'react-bootstrap';
import AddressModal from '../../components/address-modal/address-modal';
import Modal from "react-modal";
import useLogin from '../../hooks/user/useLogin';
import ReactPhoneInput from 'react-phone-input-2'
import UseWindowSize from '../../hooks/helpers/useWindowSize';
import Head from 'next/head';
import { tagButton } from '../../utils/lib/events';

const schema = yup
  .object({
    phoneNumber: yup.string().required("phone number is a required field")
      .test("Invalid phone number", value => isValidPhoneNumber(value ? '+' + value : '')),
    firstName: yup.string().required(),
    lastName: yup.string().required(),
    handle: yup.string().matches(/^[a-zA-Z0-9-_]+$/, 'Only letters, numbers, hypen and underscore allowed').required(),
    email: yup.string().required()
      .test("Invalid email", value => isValidEmailAddress(value ?? "")),
  })
  .required();

declare const window: Window &
  typeof globalThis & {
    grecaptcha: any
    recaptchaVerifier: RecaptchaVerifier,
    resendRecaptchaVerifier: RecaptchaVerifier,
    confirmationResult: ConfirmationResult
  }




function TechnicianSignUp() {

  const auth = getAuth();
  const [loading, setLoading] = useState(false);
  const { user, dbUser, isNewUser, markSignUpComplete, fetchDbUser, userExists, fetchPublicProfileByHandle } = useContext(UserContext);
  const { updateTechnicianProfile } = useUpdateProfile();
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [profileVM, setProfileVM] = useState<ProfileViewModel>();
  const [signingUp, setSigningUp] = useState(true);
  const [step, setStep] = useState<"signup" | "verification" | "address">("signup");
  const [workflowState, setWorkflowState] = useState<"notloggedin" | "loggedin" | "profileupdated" | "userconfirmation" | "addressModal" | "complete">("notloggedin");
  const [handleError, setHandleError] = useState<boolean>(false);
  const [handleFormatError, setHandleFormatError] = useState<boolean>(false);
  const [checkingHandle, setCheckingHandle] = useState<boolean>(false);
  const [currentHandleInput, setCurrentHandleInput] = useState<string>('');
  const [resendRequested, setResendRequested] = useState(false);
  const [resending, setResending] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBusySavingAddress, setIsBusySavingAddress] = useState(false);
  const { signInSendPhoneNumber, signInSendVerificationCode, resendVerificationCode, verificationCodeSent, verificationCodeError, clearVerificationError, verificationCodeResent } = useLogin();
  const screenProps = UseWindowSize();


  const { register, handleSubmit, control, setError, formState: { errors } } = useForm<ProfileViewModel>({ mode: "onSubmit", resolver: yupResolver(schema), defaultValues: { handle: uniqueName() } });

  const signUp = (data: ProfileViewModel) => {
    setPhoneNumber(e164PhoneNumber(data?.phoneNumber));
    setProfileVM({ ...data, });
    setStep("verification");
  }

  const signedIn = async () => {
    setWorkflowState("loggedin");
  }

  useEffect(() => {
    if (workflowState == "notloggedin") {
      //Nothing to do here. Other triggers will take it forward.
    } else if (workflowState == "loggedin") {
      setLoading(true);
      if (dbUser && !!dbUser.email && !!dbUser.firstName && !!dbUser?.lastName) {
        setLoading(false);
        if (dbUser?.address) {
          router.push("/");
        } else {
          setIsModalOpen(true);
          setStep("address");
          setWorkflowState("addressModal");
        }
      } else if (dbUser && !!dbUser.email && !!dbUser.firstName && !!dbUser?.lastName) {
        // setWorkflowState("settingupwallet");
        // setStep("redeem");
        setLoading(false);
        if (dbUser?.address) {
          router.push("/");
        } else {
          setIsModalOpen(true);
          setStep("address");
          setWorkflowState("addressModal");
        }
      } else if (dbUser && !!dbUser.email && !!dbUser.firstName && !!dbUser?.lastName) {
        // setWorkflowState("profileupdated");
        // setStep("walletsetup");
        setLoading(false);
        if (dbUser?.address) {
          router.push("/");
        } else {
          setIsModalOpen(true);
          setStep("address");
          setWorkflowState("addressModal");
        }
      } else if (auth?.currentUser?.uid && profileVM) {
        //save profile VM
        (async () => {
          await updateTechnicianProfile(profileVM);
          setWorkflowState("profileupdated");
          setLoading(false);
          setIsModalOpen(true);
          setStep("address");
          setWorkflowState("addressModal");
        })();
      } else if (auth?.currentUser?.uid && !dbUser) {
        //save profile VM
        (async () => {
          const localDBUser = await fetchDbUser(auth?.currentUser?.uid ?? "");
          if (!localDBUser || !localDBUser.email || !localDBUser.firstName || !localDBUser.lastName) {
            setLoading(false);
            router.push("/");
          }
        })();
      } else if (auth?.currentUser?.uid && (dbUser && (!dbUser.email || !dbUser.firstName || !dbUser.lastName) || !dbUser)) { // && !profileVM
        //User logged in, but no profile.
        setLoading(false);
      } else {
        setLoading(false);
      }
    } else if (workflowState == "profileupdated") {
      setLoading(true);
      if (dbUser) {
        setLoading(false);
        if (dbUser?.address) {
          router.push("/");
        } else {
          setIsModalOpen(true);
          setStep("address");
          setWorkflowState("addressModal");
        }
      } else {
        setLoading(false);
      }
    } else if (workflowState == "complete") {
      router.push("/");
      setLoading(false); //just in case.
    } else if (workflowState == "userconfirmation") {
      setWorkflowState("complete");
      if (dbUser?.address) {
        router.push("/");
      } else {
        setIsModalOpen(true);
        setStep("address");
        setWorkflowState("addressModal");
      }
      setLoading(false); //just in case.
    } else {

    }
  }, [auth?.currentUser?.uid, dbUser, profileVM, workflowState]);

  const onSubmit = async (data: ProfileViewModel) => {
    setLoading(true);
    const formattedPhone = '+' + data?.phoneNumber!;
    const userFound = await userExists(formattedPhone);
    if (userFound)
      setError("phoneNumber", { message: "Account already exists. Please sign in." });
    else {
      await signUp({ ...data, phoneNumber: formattedPhone, isCustomer: true, isFleetManager: false, isTechnician: true });
    }

    setLoading(false);
  }

  useEffect(() => {
    if (step === "verification") {
      (async () => {
        setLoading(true);
        window.recaptchaVerifier = new RecaptchaVerifier('recaptcha-container', {}, auth);
        await window.recaptchaVerifier.render();
        setLoading(false);
        const result = await signInSendPhoneNumber(phoneNumber, window.recaptchaVerifier);
      })();
    }
  }, [step]);

  useEffect(() => {
    clearVerificationError();
  }, [verificationCode])

  const resendCode = async () => {
    await setVerificationCode("");
    setResendRequested(true);
    setResending(true);
  }

  useEffect(() => {
    if (resending && step === "verification") {
      (async () => {
        setLoading(true);
        window.resendRecaptchaVerifier = new RecaptchaVerifier('recaptcha-container1', {}, auth);
        await window.resendRecaptchaVerifier.render();
        setLoading(false);
        await resendVerificationCode(phoneNumber, window.resendRecaptchaVerifier);
        await setResending(false);
      })();
    }
  }, [resending, step]);


  const onSubmitVerificationCode = async () => {
    setLoading(true);
    const success = await signInSendVerificationCode(verificationCode);
    if (success) {
      signedIn();
    } else {
      setLoading(false);
    }
  }

  const updateAddress = async (address: any) => {
    await setIsBusySavingAddress(true);
    await updateTechnicianProfile({ address: address });
    await setIsBusySavingAddress(false);
    router.push("/");
    setWorkflowState("complete");
  };

  useEffect(() => {
    (async () => {
      setCheckingHandle(true);
      if (currentHandleInput && !/^[a-zA-Z0-9-_]+$/.test(currentHandleInput)) {
        setHandleFormatError(true);
        setCheckingHandle(false);
        return;
      } else {
        setHandleFormatError(false);
      }
      const userByHandle = await fetchPublicProfileByHandle(currentHandleInput);
      if (userByHandle?.handle) {
        //handle it taken, check if it is us
        if (userByHandle?.userId && userByHandle?.userId == dbUser?.userId)
          setHandleError(false);
        else
          setHandleError(true);
      } else {
        setHandleError(false);
      }
      setCheckingHandle(false);
    })()
  }, [currentHandleInput]);


  return (

    <CommonStyle >
      <Head>
        <title>Paleo Car Care - Expert Auto Maintenance for Your Peace of Mind</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content="Discover the best in auto care with Paleo Car Care - Your trusted partner for top-notch car maintenance services." />
        <meta name="author" content="Paleo Car Care" />
        <meta property="og:title" content="Paleo Car Care - Technician Signup: Join Our Network for Exciting Auto Opportunities" />
        <meta property="og:image" itemProp="image" content="/images/referral-banner.png" />
        <meta name="facebook-domain-verification" content="m4o6pn81efn34qkf6t3fimmjnhbuxs" />
        <link rel="shortcut icon" href="../public/assets/images/favicon.ico" />
        <link rel="shortcut icon" href="/favicon.ico" />
      </Head>
      <div className="container-fluid p-0 image-background pb-80" style={{ backgroundImage: `url(${backgroundImage.src})`, backgroundPositionY: -70 }}>
        <div className='container py-5 '>
          <form onSubmit={handleSubmit(onSubmit)}>

            <div className='bg-danger mt-80 col-md-10 col-lg-6 py-3'>
              <h3 className='mb-0 text-center text-white light-weight'>Sign Up</h3>
            </div>
            <div className='col-md-10 col-lg-6 p-4 p-md-5 bg-white box-shadow ' >
              {/* <div className="mb-2">
                            <Link href={'/signup'} >
                                <a className='grey-text'><MdOutlineArrowBackIos size={10} /> Go Back</a>
                            </Link>
                        </div> */}
              <div className='caption'>
                <h1 className='heavy-bold'>Sign Up</h1>
              </div>
              {step === "signup" && <div className=' my-4'>
                <div className="row  mb-5">
                  <div className="col-12 col-md-6 mb-4 mb-md-0">
                    <label className="light-weight dark-text mb-3">First Name</label>
                    <input type="text" className="form-control py-3" {...register("firstName")} />
                    {errors.firstName && <p className={`errorMessage my-2`}>{errors.firstName.message}</p>}
                  </div>
                  <div className="col-12 col-md-6 mb-4 mb-md-0">
                    <label className="light-weight dark-text mb-3">Last Name</label>
                    <input type="text" className="form-control py-3" {...register("lastName")} />
                    {errors.lastName && <p className={`errorMessage my-2`}>{errors.lastName.message}</p>}
                  </div>
                </div>
                <div className="row  mb-5">
                  <div className="col-12 col-md-6">
                    <label className="light-weight dark-text mb-3">Email</label>
                    <input type="text" className="form-control py-3" {...register("email")} />
                    {errors.email && <p className={`errorMessage my-2`}>{errors.email.message}</p>}
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="light-weight dark-text mb-3">Choose a Handle (username)*</label>
                    <input
                      type="text"
                      className="form-control py-3"
                      {...register("handle")}
                      placeholder="Choose an unique Handle"
                      onChange={(e) => setCurrentHandleInput(e?.target?.value)}
                    />
                    {errors.handle && <p className={`errorMessage my-2`}>{errors.handle.message}</p>}
                    {handleFormatError && <p className="small-text mb-0 text-error">Only letters, numbers, hypen and underscore allowed</p>}
                    {handleError && <p className="small-text mb-0 text-error">This handle already exists!</p>}
                    {!checkingHandle && !handleFormatError && !handleError && currentHandleInput?.length != 0 && currentHandleInput != dbUser?.handle && <p className="small-text mb-0 text-blue">This handle is available!</p>}
                    {checkingHandle && <div style={{}}><Spinner animation="border" className="m-3" /></div>}
                  </div>
                </div>
                <div className="row  mb-5">
                  <div className="col-12 col-md-6">
                    <label className="light-weight dark-text mb-3">Phone Number</label>
                    <Controller control={control} name="phoneNumber" rules={{ required: true }}
                      render={({ field: { ref, ...field } }) => (
                        <ReactPhoneInput {...field} country={"us"} containerClass={`w-100 my-2`} inputStyle={screenProps?.windowSize?.width < 500 ? { width: '100%' } : { paddingLeft: 10, width: '100%' }} key="phoneNumber"
                          inputProps={{ ref, required: true }} autocompleteSearch />
                      )}
                    />
                    {/* <input type="text" className="form-control py-3" {...register("phoneNumber")} /> */}
                    {errors.phoneNumber && <p className={`errorMessage my-2`}>{errors.phoneNumber.message}</p>}
                  </div>
                </div>
                {loading && <Spinner animation="border" className="m-3" />}
                <div className="row  mb-5">
                  <div className="d-md-flex align-items-center col-12 col-md-4">
                    <button type="submit"  onClick={()=>{tagButton ({url:router?.pathname, item_name:"SignUp"})}} className="w-100 w-md-auto btn btn-danger py-3 px-5">Sign Up</button>
                  </div>
                  <div className="d-md-flex align-items-center col-12 col-md-8">
                    <button type="button" onClick={(e) => { router.push("/sign-in");tagButton({ url: router.pathname, item_name: 'Sign In For Existing Account '}); }} className=" w-100 w-md-auto btn border-red py-3 px-5">Already Have an Account ? Sign In</button>
                  </div>
                </div>
              </div>}
              {step === "verification" && <div className=' my-4'>
                <div id="recaptcha-container" hidden={verificationCodeSent} />
                {/* <div className='pb-3'></div> */}
                {resendRequested && <div id="recaptcha-container1" hidden={verificationCodeResent} />}
                <>
                  {<input
                    hidden={!verificationCodeSent || (resendRequested && !verificationCodeResent)}
                    id="verificationCode"
                    type="tel"
                    className="form-control py-3"
                    maxLength={6}
                    minLength={6}
                    required
                    placeholder="Enter verification code sent to your phone"
                    onChange={(e) => setVerificationCode(e.currentTarget.value)}
                  />
                  }
                  {!!verificationCodeError && <p className={`errorMessage my-2 pb-3`}>{verificationCodeError}</p>}
                  <span hidden={!verificationCodeSent || resendRequested} className="my-4 pb-3">
                    <a onClick={() => resendCode()}>Send me the code again</a>
                  </span>
                  {loading && <Spinner animation="border" className="m-3 " />}
                  <div className="row  mb-5">
                    <div className="d-md-flex align-items-center col-12 col-md-4">
                      <button type="button" onClick={(e) => { e.preventDefault(); onSubmitVerificationCode();tagButton ({url:router?.pathname, item_name:"SignUp"}) }} className="w-100 w-md-auto btn btn-danger py-3 px-5">Sign Up</button>
                    </div>
                    <div className="d-md-flex align-items-center col-12 col-md-8">
                      <button type="button" onClick={(e) => { router.push("/sign-in"); tagButton({ url: router.pathname, item_name: 'Sign In For Existing Account '}); }} className="w-100 w-md-auto btn py-3 px-5 ">Already have an account? Sign In</button>
                    </div>
                  </div>
                </>
              </div>}
              {workflowState === "addressModal" && step === "address" &&
                <Modal isOpen={isModalOpen} className="addressModal">
                  <AddressModal closeModal={() => setIsModalOpen(false)} isBusy={isBusySavingAddress} address={dbUser?.address} saveAddress={updateAddress} />
                </Modal>}
            </div>
          </form>
        </div>
      </div >
    </CommonStyle >
  )

}

export default TechnicianSignUp